/** Modale : choisir un logo JPEG/PNG/WebP dans le Coffre (comme Mail / Chat). */
import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import {
  fetchCoffreFile,
  fetchCoffreList,
  fetchCoffreUnlocked,
  formatCoffreSize,
  getCoffreAppUrl,
  isCoffreLogoName,
  type CoffreEntry,
  type CoffreZone,
} from "../../api/coffreSuite";
import "./coffreLogoPicker.css";

type CoffreLogoPickerProps = {
  open: boolean;
  busy?: boolean;
  onClose: () => void;
  onPick: (file: File) => void | Promise<void>;
};

const ZONES: { id: CoffreZone; label: string }[] = [
  { id: "vault", label: "Coffre" },
  { id: "ged", label: "GED" },
];

export function CoffreLogoPicker({ open, busy = false, onClose, onPick }: CoffreLogoPickerProps) {
  const [zone, setZone] = useState<CoffreZone>("vault");
  const [path, setPath] = useState("");
  const [folders, setFolders] = useState<CoffreEntry[]>([]);
  const [files, setFiles] = useState<CoffreEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<CoffreEntry | null>(null);

  useEffect(() => {
    if (!open) return;
    setZone("vault");
    setPath("");
    setFolders([]);
    setFiles([]);
    setLocked(false);
    setError("");
    setSelected(null);
    void reload("", "vault");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy && !pulling) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  async function reload(nextPath: string, nextZone: CoffreZone) {
    setLoading(true);
    setError("");
    setSelected(null);
    try {
      const st = await fetchCoffreUnlocked();
      if (!st.ok || !st.unlocked) {
        setLocked(true);
        setFolders([]);
        setFiles([]);
        setError(st.message || "Déverrouillez le Coffre avec votre code d’accès.");
        return;
      }
      setLocked(false);
      const result = await fetchCoffreList(nextPath, nextZone);
      if (result.error) {
        setError(result.error);
        setFolders([]);
        setFiles([]);
        return;
      }
      setPath(result.path);
      setFolders(result.entries.filter((e) => e.kind === "folder"));
      setFiles(result.entries.filter((e) => e.kind === "file" && isCoffreLogoName(e.name)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Lecture impossible.");
    } finally {
      setLoading(false);
    }
  }

  async function confirm(entry?: CoffreEntry) {
    const pick = entry ?? selected;
    if (!pick || pulling || busy) return;
    setSelected(pick);
    setPulling(true);
    setError("");
    try {
      const got = await fetchCoffreFile(pick.path, zone, pick.name);
      if (!got.file) throw new Error(got.error || "Fichier illisible.");
      await onPick(got.file);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Import impossible.");
    } finally {
      setPulling(false);
    }
  }

  if (!open) return null;

  const crumbs = path ? path.split("/").filter(Boolean) : [];
  const blocked = busy || pulling;

  return (
    <div className="fx-modal fx-co-pick" role="dialog" aria-modal="true" aria-labelledby="fx-co-pick-title">
      <button type="button" className="fx-modal__backdrop" aria-label="Fermer" disabled={blocked} onClick={onClose} />
      <div className="fx-modal__card fx-co-pick__card">
        <h2 id="fx-co-pick-title" className="fx-modal__title">
          Choisir dans le Coffre
        </h2>
        <p className="fx-co-pick__sub">JPEG, PNG ou WebP — même parcours que Mail et Chat.</p>
        <div className="fx-co-pick__tabs" role="tablist">
          {ZONES.map((z) => (
            <button
              key={z.id}
              type="button"
              role="tab"
              aria-selected={zone === z.id}
              className={zone === z.id ? "is-on" : ""}
              disabled={blocked}
              onClick={() => {
                if (z.id === zone) return;
                setZone(z.id);
                void reload("", z.id);
              }}
            >
              {z.label}
            </button>
          ))}
        </div>
        <div className="fx-co-pick__crumbs">
          <button type="button" disabled={blocked} onClick={() => void reload("", zone)}>
            {zone === "ged" ? "GED" : "Coffre"}
          </button>
          {crumbs.map((part, i) => {
            const crumb = crumbs.slice(0, i + 1).join("/");
            return (
              <span key={crumb}>
                <em>/</em>
                <button type="button" disabled={blocked} onClick={() => void reload(crumb, zone)}>
                  {part}
                </button>
              </span>
            );
          })}
        </div>
        {error ? (
          <p className="fx-co-pick__err" role="alert">
            {error}
            {locked ? (
              <>
                {" "}
                <a href={getCoffreAppUrl()} target="_blank" rel="noopener noreferrer">
                  Ouvrir le Coffre
                </a>
              </>
            ) : null}
          </p>
        ) : null}
        <div className="fx-co-pick__list">
          {loading && folders.length === 0 && files.length === 0 ? (
            <p className="fx-co-pick__empty">Chargement…</p>
          ) : folders.length === 0 && files.length === 0 ? (
            <p className="fx-co-pick__empty">{locked ? "Coffre verrouillé." : "Aucune image ici."}</p>
          ) : (
            <ul>
              {folders.map((folder) => (
                <li key={`d-${folder.path}`}>
                  <button type="button" disabled={blocked} onClick={() => void reload(folder.path, zone)}>
                    <FolderIcon />
                    <span>{folder.name}</span>
                  </button>
                </li>
              ))}
              {files.map((file) => {
                const on = selected?.path === file.path;
                return (
                  <li key={`f-${file.path}`}>
                    <button
                      type="button"
                      className={on ? "is-on" : ""}
                      disabled={blocked}
                      onClick={() => setSelected(on ? null : file)}
                      onDoubleClick={() => void confirm(file)}
                    >
                      <FileIcon />
                      <span>
                        <strong>{file.name}</strong>
                        {file.size ? <em>{formatCoffreSize(file.size)}</em> : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="fx-modal__actions">
          <Button variant="ghost" onClick={onClose} disabled={blocked}>
            Annuler
          </Button>
          <Button disabled={blocked || locked || !selected} onClick={() => void confirm()}>
            {pulling ? "Préparation…" : "Utiliser ce logo"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FolderIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <circle cx="12" cy="11" r="3" />
      <path d="M6 18l3.2-3.2a1 1 0 0 1 1.4 0L14 18l1.4-1.4a1 1 0 0 1 1.4 0L20 18" />
    </svg>
  );
}
